import { useState, useEffect } from 'react';
import { X, ArrowLeft, FolderOpen, Folder, FileText, FileCode, Globe, Presentation, FileSpreadsheet, Download, Database, FileJson, Sheet, Table, File, FileArchive, FileImage } from 'lucide-react';
import { API_ENDPOINTS } from '../config';

const TAURUS_API = API_ENDPOINTS.taurus;

export default function FilePanel({ isOpen, onClose, currentPath, onPathChange, showNotification }) {
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [parentPath, setParentPath] = useState(null);
  const [loading, setLoading] = useState(false);

  // Load files when panel opens or path changes
  useEffect(() => {
    if (!isOpen) return;

    const abortController = new AbortController();

    const fetchFiles = async () => {
      setLoading(true);
      try {
        const url = currentPath
          ? `${TAURUS_API}/api/files?path=${encodeURIComponent(currentPath)}`
          : `${TAURUS_API}/api/files`;
        const response = await fetch(url, { signal: abortController.signal });
        const data = await response.json();
        setFiles(data.files || []);
        setFolders(data.folders || []);
        setParentPath(data.parentPath);
      } catch (err) {
        // Ignore abort errors
        if (err.name !== 'AbortError') {
          console.error('Error loading files:', err);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchFiles();

    // Cleanup: abort fetch if component unmounts or dependencies change
    return () => abortController.abort();
  }, [isOpen, currentPath]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);


  // Removed loadFiles - now handled directly in useEffect with abort controller

  const handleFolderClick = (folderPath) => {
    onPathChange(folderPath);
  };

  const handleBackClick = () => {
    if (parentPath !== null) {
      onPathChange(parentPath);
    }
  };


  const handleOpenFolder = async () => {
    try {
      const response = await fetch(`${TAURUS_API}/api/files/open-folder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ path: currentPath }),
      });
      if (response.ok && showNotification) {
        showNotification('success', 'Folder opened in Explorer');
      }
    } catch (err) {
      console.error('Failed to open folder:', err);
      if (showNotification) {
        showNotification('error', 'Failed to open folder');
      }
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleString();
  };

  const getFileIcon = (extension) => {
    switch (extension?.toLowerCase()) {
      // Database files
      case '.db':
      case '.sqlite':
      case '.sqlite3':
      case '.sql':
        return Database;
      // Spreadsheet files
      case '.csv':
        return Table;
      case '.xlsx':
      case '.xls':
        return Sheet;
      // Data files
      case '.json':
        return FileJson;
      // Document files
      case '.pdf':
        return File;
      case '.docx':
      case '.doc':
        return FileText;
      case '.txt':
      case '.md':
        return FileText;
      // Presentation files
      case '.pptx':
      case '.ppt':
        return Presentation;
      // Web files
      case '.html':
      case '.htm':
        return Globe;
      // Code files
      case '.py':
      case '.js':
      case '.jsx':
      case '.ts':
      case '.tsx':
      case '.java':
      case '.cpp':
      case '.c':
      case '.cs':
      case '.go':
      case '.rs':
      case '.rb':
      case '.php':
        return FileCode;
      // Archive files
      case '.zip':
      case '.tar':
      case '.gz':
      case '.rar':
      case '.7z':
        return FileArchive;
      // Image files
      case '.png':
      case '.jpg':
      case '.jpeg':
      case '.gif':
      case '.svg':
      case '.webp':
      case '.ico':
        return FileImage;
      default:
        return FileText;
    }
  };

  const getFileTag = (extension) => {
    switch (extension?.toLowerCase()) {
      // Database files
      case '.db':
      case '.sqlite':
      case '.sqlite3':
        return { label: 'DB', color: 'bg-teal-100 dark:bg-teal-900 text-teal-700 dark:text-teal-300' };
      case '.sql':
        return { label: 'SQL', color: 'bg-teal-100 dark:bg-teal-900 text-teal-700 dark:text-teal-300' };
      // Spreadsheet files
      case '.csv':
        return { label: 'CSV', color: 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300' };
      case '.xlsx':
        return { label: 'XLSX', color: 'bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300' };
      case '.xls':
        return { label: 'XLS', color: 'bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300' };
      // Data files
      case '.json':
        return { label: 'JSON', color: 'bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300' };
      // Document files
      case '.pdf':
        return { label: 'PDF', color: 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300' };
      case '.docx':
        return { label: 'DOCX', color: 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300' };
      case '.doc':
        return { label: 'DOC', color: 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300' };
      case '.txt':
        return { label: 'TXT', color: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300' };
      case '.md':
        return { label: 'MD', color: 'bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300' };
      // Presentation files
      case '.pptx':
        return { label: 'PPTX', color: 'bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300' };
      case '.ppt':
        return { label: 'PPT', color: 'bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300' };
      // Web files
      case '.html':
      case '.htm':
        return { label: 'HTML', color: 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300' };
      // Code files
      case '.py':
        return { label: 'PY', color: 'bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300' };
      case '.js':
        return { label: 'JS', color: 'bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300' };
      case '.jsx':
        return { label: 'JSX', color: 'bg-cyan-100 dark:bg-cyan-900 text-cyan-700 dark:text-cyan-300' };
      case '.ts':
        return { label: 'TS', color: 'bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300' };
      case '.tsx':
        return { label: 'TSX', color: 'bg-sky-100 dark:bg-sky-900 text-sky-700 dark:text-sky-300' };
      case '.java':
        return { label: 'JAVA', color: 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300' };
      case '.cpp':
      case '.c':
        return { label: 'C++', color: 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300' };
      case '.cs':
        return { label: 'C#', color: 'bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300' };
      case '.go':
        return { label: 'GO', color: 'bg-cyan-100 dark:bg-cyan-900 text-cyan-700 dark:text-cyan-300' };
      case '.rs':
        return { label: 'RUST', color: 'bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300' };
      case '.rb':
        return { label: 'RUBY', color: 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300' };
      case '.php':
        return { label: 'PHP', color: 'bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300' };
      // Archive files
      case '.zip':
        return { label: 'ZIP', color: 'bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300' };
      case '.tar':
      case '.gz':
        return { label: 'TAR', color: 'bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300' };
      case '.rar':
        return { label: 'RAR', color: 'bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300' };
      case '.7z':
        return { label: '7Z', color: 'bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300' };
      // Image files
      case '.png':
        return { label: 'PNG', color: 'bg-pink-100 dark:bg-pink-900 text-pink-700 dark:text-pink-300' };
      case '.jpg':
      case '.jpeg':
        return { label: 'JPG', color: 'bg-pink-100 dark:bg-pink-900 text-pink-700 dark:text-pink-300' };
      case '.gif':
        return { label: 'GIF', color: 'bg-fuchsia-100 dark:bg-fuchsia-900 text-fuchsia-700 dark:text-fuchsia-300' };
      case '.svg':
        return { label: 'SVG', color: 'bg-violet-100 dark:bg-violet-900 text-violet-700 dark:text-violet-300' };
      case '.webp':
        return { label: 'WEBP', color: 'bg-pink-100 dark:bg-pink-900 text-pink-700 dark:text-pink-300' };
      case '.ico':
        return { label: 'ICO', color: 'bg-rose-100 dark:bg-rose-900 text-rose-700 dark:text-rose-300' };
      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/20 dark:bg-black/50 z-30"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="fixed right-0 top-0 bottom-0 w-96 bg-white dark:bg-gray-900 shadow-2xl z-40 flex flex-col border-l border-gray-200 dark:border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">File Explorer</h2>
          <button
            onClick={onClose}
            className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Current Path and Actions */}
        <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <button
                onClick={handleBackClick}
                disabled={parentPath === null}
                className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 transition-colors p-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/30 disabled:opacity-30 disabled:cursor-not-allowed"
                title={parentPath !== null ? "Go back" : "At root"}
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <span className="text-xs text-gray-600 dark:text-gray-400 truncate">
                {currentPath || 'Root'}
              </span>
            </div>
            <button
              onClick={handleOpenFolder}
              className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 transition-colors px-2 py-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/30"
              title="Open in file explorer"
            >
              <FolderOpen className="w-3 h-3" />
              <span>Open</span>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 dark:border-blue-400 border-t-transparent"></div>
            </div>
          ) : (
            <div className="p-2">
              {/* Folders */}
              {folders.map((folder) => (
                <button
                  key={folder.path}
                  onClick={() => handleFolderClick(folder.path)}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-left transition-colors group"
                >
                  <Folder className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                  <span className="text-sm text-gray-900 dark:text-gray-100 truncate">{folder.name}</span>
                </button>
              ))}

              {/* Files */}
              {files.map((file, index) => {
                const Icon = getFileIcon(file.extension);
                const tag = getFileTag(file.extension);

                return file.path ? (
                  <a
                    key={`file-${index}`}
                    href={file.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors group cursor-pointer"
                  >
                    <Icon className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-900 dark:text-gray-100 truncate">{file.name}</span>
                        {tag && (
                          <span className={`text-xs px-1.5 py-0.5 rounded ${tag.color} font-medium`}>
                            {tag.label}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                        <span>{formatFileSize(file.size)}</span>
                        <span>•</span>
                        <span>{formatDate(file.modified)}</span>
                      </div>
                    </div>
                  </a>
                ) : (
                  <div
                    key={`file-${index}`}
                    className="flex items-center gap-2 px-3 py-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors group"
                  >
                    <Icon className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-900 dark:text-gray-100 truncate">{file.name}</span>
                        {tag && (
                          <span className={`text-xs px-1.5 py-0.5 rounded ${tag.color} font-medium`}>
                            {tag.label}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                        <span>{formatFileSize(file.size)}</span>
                        <span>•</span>
                        <span>{formatDate(file.modified)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}

              {folders.length === 0 && files.length === 0 && (
                <div className="text-center text-gray-500 dark:text-gray-400 text-sm py-8">
                  No files or folders
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
